import axios, { AxiosError } from 'axios';
import { Sentry } from '../lib/sentry';
import { mcpService } from './mcpService';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * True when an error came back from the API as a permission denial (HTTP 403).
 * Callers use this to branch error UIs — a 403 is "no access" (show Forbidden UI),
 * anything else (500, network failure, timeout) is "connection problem" (show retry).
 */
export function isForbiddenError(err: unknown): boolean {
  if (!err) return false;
  const e = err as AxiosError;
  return e.isAxiosError === true && e.response?.status === 403;
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // The instance default above declares JSON for every request, and axios v1 reacts to a
    // JSON Content-Type by serialising a FormData body into a JSON object — the files are
    // dropped and the server sees `{"file":{}}` instead of a multipart upload. Clearing the
    // header here lets the browser set `multipart/form-data` with its own boundary, which
    // is the only way the boundary can be correct.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // Only redirect if it's a 401 and we're not already on the login page.
    // Use startsWith to handle query-string variants like /login?redirect=...
    if (status === 401) {
      const isLoginPage = window.location.pathname.startsWith('/login');
      if (!isLoginPage) {
        localStorage.removeItem('token');
        mcpService.clearActiveKey();
        // Let the component handle the redirect/error message
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }

    if (status === 402) {
      // Invalidate the subscription cache so the banner reflects reality immediately.
      try {
        const qc = (window as unknown as { __qc?: { invalidateQueries: (opts: { queryKey: unknown[] }) => void } }).__qc;
        if (qc) {
          // Use inline key to avoid circular dep: api → useSubscription → subscriptionService → api
          qc.invalidateQueries({ queryKey: ['subscription'] });
        }
      } catch {
        /* non-fatal */
      }

      // Notify the React tree to surface a toast — mirrors the auth:unauthorized pattern.
      window.dispatchEvent(
        new CustomEvent('subscription:upgrade-required', {
          detail: {
            message:
              (error.response?.data as { message?: string } | undefined)?.message ??
              'Pro subscription required for this action.',
          },
        })
      );
    }

    // Report 5xx errors to Sentry
    if (status && status >= 500) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status,
          data: error.response?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

export default api;
